import React from 'react'
import './GeneralSection.styles.css'

interface NotificationPreferences {
  email: boolean
  sms: boolean
  push: boolean
}

interface GeneralSectionProps {
  maxExpertises: number
  language: string
  notifications?: NotificationPreferences
  onChange: (updates: {
    maxExpertisesPerDay?: number
    preferredLanguage?: string
    notificationPreferences?: NotificationPreferences
  }) => void
}

export const GeneralSection: React.FC<GeneralSectionProps> = ({ 
  maxExpertises, 
  language, 
  notifications = { email: true, sms: true, push: false },
  onChange 
}) => {
  const handleMaxExpertisesChange = (value: number) => {
    const newValue = Math.max(1, Math.min(10, value))
    onChange({ maxExpertisesPerDay: newValue })
  }

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ preferredLanguage: e.target.value })
  }

  const handleNotificationToggle = (type: keyof NotificationPreferences) => {
    onChange({
      notificationPreferences: {
        ...notifications,
        [type]: !notifications[type]
      }
    })
  }

  return (
    <div className="general-section">
      <div className="section-header">
        <h2 className="section-title">Paramètres généraux</h2>
        <p className="section-description">
          Configurez vos préférences générales et notifications
        </p>
      </div>
      
      <div className="settings-list">
        {/* Max expertises per day */}
        <div className="setting-row">
          <div className="setting-info">
            <div className="setting-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="M18 17V9" />
                <path d="M13 17V5" />
                <path d="M8 17v-3" />
              </svg>
            </div>
            <div className="setting-details">
              <span className="setting-title">
                Nombre maximum d'expertises par jour
              </span>
              <span className="setting-description">
                Limite quotidienne d'expertises acceptées
              </span>
            </div>
          </div>
          <div className="counter">
            <button 
              className="counter-btn"
              onClick={() => handleMaxExpertisesChange(maxExpertises - 1)}
              disabled={maxExpertises <= 1}
            >
              −
            </button>
            <span className="counter-value">{maxExpertises}</span>
            <button 
              className="counter-btn"
              onClick={() => handleMaxExpertisesChange(maxExpertises + 1)}
              disabled={maxExpertises >= 10}
            >
              +
            </button>
          </div>
        </div>

        {/* Preferred language */}
        <div className="setting-row">
          <div className="setting-info">
            <div className="setting-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </div>
            <div className="setting-details">
              <span className="setting-title">
                Langue préférée
              </span>
              <span className="setting-description">
                Langue de communication principale
              </span>
            </div>
          </div>
          <select 
            value={language}
            onChange={handleLanguageChange}
            className="select-modern"
          >
            <option value="fr">Français</option>
            <option value="nl">Nederlands</option>
            <option value="en">English</option>
            <option value="de">Deutsch</option>
          </select>
        </div>

        {/* Notification preferences */}
        <div className="setting-group">
          <h3 className="group-title">Préférences de notification</h3>
          <p className="group-description">
            Choisissez comment vous souhaitez être notifié
          </p>
          
          <div className="notification-settings">
            {/* Email notifications */}
            <div className="notification-item">
              <label className="notification-label">
                <div className="notification-info">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-10 5L2 7" />
                  </svg>
                  <div>
                    <span className="notification-title">Notifications par email</span>
                    <span className="notification-desc">Recevez des alertes par email</span>
                  </div>
                </div>
                <label className="expert-pref-toggle-switch">
                  <input 
                    type="checkbox"
                    checked={notifications.email}
                    onChange={() => handleNotificationToggle('email')}
                  />
                  <span className="expert-pref-toggle-slider"></span>
                </label>
              </label>
            </div>

            {/* SMS notifications */}
            <div className="notification-item">
              <label className="notification-label">
                <div className="notification-info">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <path d="M12 18h.01" />
                  </svg>
                  <div>
                    <span className="notification-title">Notifications par SMS</span>
                    <span className="notification-desc">Recevez des alertes par SMS</span>
                  </div>
                </div>
                <label className="expert-pref-toggle-switch">
                  <input 
                    type="checkbox"
                    checked={notifications.sms}
                    onChange={() => handleNotificationToggle('sms')}
                  />
                  <span className="expert-pref-toggle-slider"></span>
                </label>
              </label>
            </div>

            {/* Push notifications */}
            <div className="notification-item">
              <label className="notification-label">
                <div className="notification-info">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  <div>
                    <span className="notification-title">Notifications push</span>
                    <span className="notification-desc">Recevez des notifications dans l'app</span>
                  </div>
                </div>
                <label className="expert-pref-toggle-switch">
                  <input 
                    type="checkbox"
                    checked={notifications.push}
                    onChange={() => handleNotificationToggle('push')}
                  />
                  <span className="expert-pref-toggle-slider"></span>
                </label>
              </label>
            </div>
          </div>
        </div>

        {/* Additional settings info */}
        <div className="settings-info">
          <div className="info-card">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
            <p>
              Ces paramètres s'appliquent immédiatement et affectent la façon dont les missions vous sont assignées et comment vous êtes notifié.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}